import React, { useState, useEffect } from 'react';
import { Switch } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { AppDispatch, RootState } from '../../../redux/store';
import { updateLoyaltyConfig } from '../../../redux/Loyalty';
import { LoyaltyConfig } from '../types';
import notification from '../../../commonComponents/Notification/Notification';
import {
  ConfigCard,
  ConfigHeader,
  ConfigHeaderContent,
  IconWrapper,
  ToggleContainer,
  ToggleContent,
  ToggleInfo,
  ToggleLabel,
  ToggleDescription,
  ConfigGrid,
  ConfigItem,
  ConfigItemLabel,
  ConfigItemValue,
  StyledInput,
  DisabledState,
  DisabledIcon,
  DisabledTitle,
  DisabledDescription,
  ActionButtons,
  PrimaryButton,
  CancelButton,
  HeaderText,
  EditButton,
} from '../styles';

const Configuration: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { config, loading } = useSelector((state: RootState) => state.loyalty);
  const { isDarkMode } = useSelector((state: RootState) => state.theme);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState<LoyaltyConfig>({
    loyaltyEnabled: false,
    loyaltyRedeemRatio: '',
    loyaltySpendRatio: '',
    minSpentForLoyalty: '',
    minSpentForRedemption: '',
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isMerchant = user.role === 'merchant';

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const theme: DefaultTheme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  const handleInputChange = (name: keyof LoyaltyConfig, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await dispatch(updateLoyaltyConfig(formData)).unwrap();
      setIsEditing(false);
      notification.success('Loyalty configuration updated successfully!');
    } catch (error: any) {
      notification.error(error || 'Failed to save loyalty configuration');
    }
  };

  const handleCancel = () => {
    if (config) {
      setFormData(config);
    }
    setIsEditing(false);
  };

  if (!isMerchant) {
    return null;
  }

  const configItems = [
    {
      label: 'Loyalty Redeem Ratio',
      name: 'loyaltyRedeemRatio' as keyof LoyaltyConfig,
      bgColor: isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
      borderColor: isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)',
      textColor: isDarkMode ? '#22c55e' : '#16a34a',
    },
    {
      label: 'Loyalty Spend Ratio',
      name: 'loyaltySpendRatio' as keyof LoyaltyConfig,
      bgColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
      textColor: isDarkMode ? '#3b82f6' : '#2563eb',
    },
    {
      label: 'Min Spent for Loyalty',
      name: 'minSpentForLoyalty' as keyof LoyaltyConfig,
      bgColor: isDarkMode ? 'rgba(249, 115, 22, 0.1)' : 'rgba(249, 115, 22, 0.05)',
      borderColor: isDarkMode ? 'rgba(249, 115, 22, 0.3)' : 'rgba(249, 115, 22, 0.2)',
      textColor: isDarkMode ? '#f97316' : '#ea580c',
    },
    {
      label: 'Min Spent for Redemption',
      name: 'minSpentForRedemption' as keyof LoyaltyConfig,
      bgColor: isDarkMode ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.05)',
      borderColor: isDarkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.2)',
      textColor: isDarkMode ? '#a855f7' : '#9333ea',
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <ConfigCard>
      <ConfigHeader>
        <ConfigHeaderContent>
          <IconWrapper
            $bgColor={isDarkMode ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.05)'}
            $color={isDarkMode ? '#a855f7' : '#9333ea'}
          >
            <FontAwesomeIcon icon={faGift} style={{ fontSize: '18px' }} />
          </IconWrapper>
          <HeaderText $isDarkMode={isDarkMode}>
            Loyalty Configuration
          </HeaderText>
        </ConfigHeaderContent>
        {!isEditing && (
          <EditButton
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
            type="primary"
          >
            Edit Configuration
          </EditButton>
        )}
      </ConfigHeader>

      <ToggleContainer>
        <ToggleContent>
          <ToggleInfo>
            <ToggleLabel>Enable Loyalty Program</ToggleLabel>
            <ToggleDescription>
              Turn loyalty program on/off for your customers
            </ToggleDescription>
          </ToggleInfo>
          <Switch
            checked={formData.loyaltyEnabled}
            onChange={(checked) => handleInputChange('loyaltyEnabled', checked)}
            disabled={!isEditing}
            style={{ backgroundColor: formData.loyaltyEnabled ? '#52c41a' : undefined }}
          />
        </ToggleContent>
      </ToggleContainer>

      {formData.loyaltyEnabled ? (
        <>
          <ConfigGrid>
            {configItems.map((item) => (
              <ConfigItem key={item.name} $bgColor={item.bgColor} $borderColor={item.borderColor}>
                <ConfigItemLabel>{item.label}</ConfigItemLabel>
                {isEditing ? (
                  <StyledInput
                    name={item.name}
                    value={formData[item.name] as string}
                    onChange={(e) => handleInputChange(item.name, e.target.value)}
                    placeholder={`Enter ${item.label.toLowerCase()}`}
                    type="number"
                    step="0.01"
                    min="0"
                  />
                ) : (
                  <ConfigItemValue style={{ color: item.textColor }}>
                    {item.name.includes('Spent') && formData[item.name]
                      ? `$${formData[item.name]}`
                      : formData[item.name] || '0'}
                  </ConfigItemValue>
                )}
              </ConfigItem>
            ))}
          </ConfigGrid>

          {isEditing && (
            <ActionButtons>
              <CancelButton onClick={handleCancel} icon={<CloseOutlined />}>
                Cancel
              </CancelButton>
              <PrimaryButton
                onClick={handleSave}
                icon={<SaveOutlined />}
                loading={loading.update}
              >
                Save Changes
              </PrimaryButton>
            </ActionButtons>
          )}
        </>
      ) : (
        <DisabledState>
          <DisabledIcon>
            <FontAwesomeIcon icon={faGift} style={{ fontSize: '32px' }} />
          </DisabledIcon>
          <DisabledTitle>Loyalty program is currently disabled</DisabledTitle>
          <DisabledDescription>
            Enable loyalty to start rewarding your customers
          </DisabledDescription>
        </DisabledState>
      )}
    </ConfigCard>
    </ThemeProvider>
  );
};

export default Configuration;
